import Promise from 'bluebird'
import _ from 'lodash'
import React from 'react'
import { browserHistory } from 'browser/history'

import { getDeferredUser } from 'shared-libs/helpers/utils'
import { Entity } from 'shared-libs/models/entity'

import apis from 'browser/app/models/apis'
import { Settings } from 'browser/app/models/settings'
import ComponentsMap from 'browser/components'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { EntityRenderer } from 'shared-libs/components/entity/renderer'

interface IEntityFormBlockProps extends IBaseProps {
  componentsMap?: Record<string, any>
  defaultValue?: object
  entity: Entity
  errors?: object
  onClose?: (entity: Entity) => void
  onSave: (entity: Entity) => Promise<any>
  onSelect?: (entity: Entity) => void
  settings: Settings
  state?: object
  uiContext?: object
  uiSchemaPath: string
}

interface IEntityFormBlockStates {
  entity: Entity
  errors: any
  title: string
  recipients: any[]
}

export class EntityFormBlock extends React.Component<IEntityFormBlockProps, IEntityFormBlockStates> {

  public static defaultProps: Partial<IEntityFormBlockProps> = {
    uiContext: {
      density: 'collapse',
      isHorizontalLayout: true,
    },
  }

  private store: any

  constructor(props: IEntityFormBlockProps) {
    super(props)
    this.store = apis.getStore()
    const { entity } = props
    const title = `New ${entity.entityType}`
    this.state = {
      entity,
      errors: props.errors,
      recipients: [],
      title,
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IEntityFormBlockProps) {
    if (this.props.errors !== nextProps.errors) {
      this.setState({ errors: nextProps.errors })
    }
    if (this.props.entity !== nextProps.entity) {
      this.setState({ entity: nextProps.entity })
    }
  }

  public render() {
    const { defaultValue, settings, state, uiSchemaPath, uiContext } = this.props
    const { entity, errors } = this.state
    // TODO(Peter): jank. I guess we need defaultValue here for the document-creation-flow
    const rendererState = {
      defaultValue,
      settings,
      ...this.state,
      ...state,
    }
    const renderContext = {
      ...uiContext,
      onClose: this.handleOnClose,
    }
    // Note that most entities need isHorizontalLayout, except load entity.
    return (
      <EntityRenderer
        applyDefaults={true}
        actions={this}
        componentsMap={this.props.componentsMap || ComponentsMap}
        context={renderContext}
        errors={errors}
        onChangeComplete={this.handleOnChange}
        state={rendererState}
        uiSchemaPath={uiSchemaPath}
        value={entity}
      />
    )
  }

  /****************************************************************************/
  // UI Schema actions
  /****************************************************************************/

  private handleOnClose = () => {
    const { entity } = this.state
    this.props.onClose(entity)
  }

  private handleOnSelect = (entity: Entity) => {
    const { onSelect } = this.props
    onSelect(entity)
  }

  private handleOnChange = (entity: Entity) => {
    const { recipients } = this.state
    // TODO (David): Maybe this needs to be more generic since orders,
    // capacity, and anything can use the entity-form-block
    // This should be moved to the recipients field.
    const result = entity.resolveSubschemaByPath('uiSchema.web.entityCreationSection')
    const defaultRecipients = _.get(result, 'schema.metadata.defaultRecipients', [])
    this.setState({
      entity,
      recipients: _.union(defaultRecipients, recipients),
      title: `New ${entity.entityType}`,
    })
  }

  private handleOnDelete = () => {
    const { entity } = this.state
    entity.delete().then(() => {
      this.handleOnClose()
    })
  }

  private handleOnSave = (): Promise<any> => {
    const { onSave } = this.props
    const { entity, recipients } = this.state
    return Promise.resolve(entity.validate()).then((errors) => {
      if (!_.isEmpty(errors)) {
        this.setState({ errors })
        return Promise.reject(errors)
      }

      const shares = recipients.map((recipient) => entity.addShare(getDeferredUser(recipient)))

      return Promise.all([onSave(entity), ...shares])
    })
  }

  private handleRecipientsChange = (recipients: IEntityFormBlockStates['recipients']) => {
    this.setState({ recipients })
  }

  private handleOnSendInvite = () => {
    const { entity } = this.state
    entity.set('invite.status', 'Sent')
    void this.handleOnSave()
  }
}
